import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Spinner,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import {
  FiBriefcase,
  FiClock,
  FiMail,
  FiMapPin,
  FiPhone,
  FiUser,
} from "react-icons/fi";
import { BsBuilding } from "react-icons/bs";
import { useState } from "react";
import { extractApiErrorDetail } from "../../services/errorHandler";
import { useEssDashboard } from "../../services/essService";

const LABEL_MUTED = "#64748b";

const formatMoney = (raw) => {
  if (raw == null || raw === "") return "—";
  const n = Number(String(raw).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(raw);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

function WorkFact({ icon, label, children }) {
  if (!children) return null;
  return (
    <HStack align="flex-start" spacing={3} py={1.5}>
      <Icon as={icon} mt={0.5} color="blue.600" boxSize={4} flexShrink={0} />
      <Box minW={0}>
        <Text fontSize="12px" color={LABEL_MUTED} fontWeight="600" letterSpacing="0.02em">
          {label}
        </Text>
        <Text fontSize="14px" color="gray.800" fontWeight="500">
          {children}
        </Text>
      </Box>
    </HStack>
  );
}

function SoftCard({ title, sub, children, ...rest }) {
  return (
    <Box
      bg="white"
      borderRadius="xl"
      boxShadow="sm"
      px={5}
      py={5}
      {...rest}
    >
      {title && (
        <Text fontSize="sm" fontWeight="700" color="gray.800" letterSpacing="-0.01em">
          {title}
        </Text>
      )}
      {sub && (
        <Text fontSize="xs" color="gray.500" mt={1} mb={4}>
          {sub}
        </Text>
      )}
      {!sub && title && <Box mb={1} />}
      {children}
    </Box>
  );
}

const EssProfile = () => {
  const toast = useToast();
  const [salaryVisible, setSalaryVisible] = useState(false);

  const {
    essDashboard,
    essDashboardIsLoading,
    essDashboardError,
    refetchEssDashboard,
    linked,
    portal,
  } = useEssDashboard();

  const errDetail = essDashboardError ? extractApiErrorDetail(essDashboardError) : null;

  if (essDashboardIsLoading && !essDashboard) {
    return (
      <Flex justify="center" py={24}>
        <Spinner size="lg" color="blue.500" thickness="3px" />
      </Flex>
    );
  }

  if (errDetail?.code === "ESS_ONLY") {
    return (
      <Alert status="warning" borderRadius="xl" maxW="lg" boxShadow="sm">
        <AlertIcon />
        <Box>
          <Text fontWeight="600">{errDetail.message || "Wrong account type"}</Text>
          <Text fontSize="sm" mt={2} color="gray.700">
            For the admin portal, sign out and use an administrator account.
          </Text>
        </Box>
      </Alert>
    );
  }

  if (errDetail?.code === "NO_LINKED_EMPLOYEE" && !portal) {
    return (
      <VStack align="stretch" spacing={4} maxW="md">
        <Alert status="warning" borderRadius="xl">
          <AlertIcon />
          {errDetail.message || "No employee record is linked to this account."}
        </Alert>
        <Button size="sm" colorScheme="blue" variant="outline" onClick={() => refetchEssDashboard()}>
          Retry
        </Button>
      </VStack>
    );
  }

  if (essDashboardError && !essDashboard) {
    return (
      <VStack align="stretch" spacing={4} maxW="md">
        <Alert status="error" borderRadius="xl">
          <AlertIcon />
          {errDetail?.message || "Could not load your profile."}
        </Alert>
        <Button size="sm" colorScheme="blue" onClick={() => refetchEssDashboard()}>
          Retry
        </Button>
      </VStack>
    );
  }

  const hero = portal?.hero ?? {};
  const work = portal?.work ?? {};
  const personal = portal?.personal ?? {};
  const isLinked = linked !== false;

  const name = hero.display_name || "Employee";
  const designation = hero.designation || "";
  const departmentName = hero.department || "";
  const employeeBizId = hero.employee_id || personal.employee_id || "";
  const email = personal.email || "—";
  const phone = personal.phone_number || "—";
  const salaryRaw = personal.salary;
  const heroSub = [designation, departmentName].filter(Boolean).join(" • ");

  return (
    <VStack align="stretch" spacing={{ base: 6, md: 8 }} maxW="1024px" pb={10}>
      <Box>
        <Heading size="lg" letterSpacing="-0.02em">
          Profile
        </Heading>
        <Text fontSize="sm" color="gray.600" mt={1}>
          Your work and contact details from HR. To change anything, use Profile help or contact HR.
        </Text>
      </Box>

      {!isLinked && (
        <Alert status="info" borderRadius="lg" size="sm">
          <AlertIcon />
          <Text fontSize="sm">
            Your account isn’t linked to an employee record yet. Details below may be incomplete until HR completes setup.
          </Text>
        </Alert>
      )}

      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        px={5}
        py={5}
        borderWidth="1px"
        borderColor="gray.100"
      >
        <Text fontSize="sm" fontWeight="700" color="gray.800">
          {name}
        </Text>
        {heroSub && (
          <Text fontSize="sm" color="gray.600" mt={1}>
            {heroSub}
          </Text>
        )}
        <Button
          mt={4}
          size="sm"
          variant="ghost"
          leftIcon={<FiUser />}
          onClick={() =>
            toast({
              title: "Profile updates",
              description: "To change your details, contact HR or your line manager.",
              status: "info",
              duration: 4000,
            })
          }
        >
          Profile help
        </Button>
      </Box>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="start">
        <SoftCard title="Work">
          <WorkFact icon={BsBuilding} label="Department">
            {work.department}
          </WorkFact>
          <WorkFact icon={FiUser} label="Reports to">
            {work.reporting_senior}
          </WorkFact>
          <WorkFact icon={FiClock} label="Shift">
            {work.shift}
          </WorkFact>
          <WorkFact icon={FiBriefcase} label="Branch">
            {work.branch}
          </WorkFact>
          <WorkFact icon={FiMapPin} label="Location">
            {work.location}
          </WorkFact>
        </SoftCard>

        <SoftCard title="Personal">
          <WorkFact icon={FiMail} label="Email">
            {email !== "—" ? email : null}
          </WorkFact>
          <WorkFact icon={FiPhone} label="Phone">
            {phone !== "—" ? phone : null}
          </WorkFact>
          <WorkFact icon={FiUser} label="Employee ID">
            {employeeBizId}
          </WorkFact>
          {salaryRaw != null && salaryRaw !== "" && (
            <Box pt={2}>
              <HStack justify="space-between" align="center">
                <HStack spacing={2}>
                  <Text fontSize="12px" color={LABEL_MUTED} fontWeight="600">
                    Salary
                  </Text>
                  <Button
                    size="xs"
                    variant="link"
                    colorScheme="blue"
                    onClick={() => setSalaryVisible((v) => !v)}
                  >
                    {salaryVisible ? "Hide" : "Show"}
                  </Button>
                </HStack>
              </HStack>
              <Text fontSize="14px" color="gray.800" fontWeight="600" letterSpacing="0.02em">
                {salaryVisible ? `₦${formatMoney(salaryRaw)}` : "••••••"}
              </Text>
            </Box>
          )}
        </SoftCard>
      </SimpleGrid>
    </VStack>
  );
};

export default EssProfile;
